import React from "react";
import {
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Typography,
} from "@mui/material";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import {
  formatOriginWithInvoices,
  formatScheduledDate,
  getRouteName,
  getShipmentLoads,
} from "app/components/Dashboard/kanbanDisplay";

type MobileOrderCardProps = {
  item: any;
  stageColor?: string;
  userTimezone: string;
  isSelected: boolean;
  onToggle: (itemId: number, checked: boolean) => void;
  onMove: (item: any) => void;
};

export const MobileOrderCard = React.memo(function MobileOrderCard({
  item,
  stageColor = "#808080",
  userTimezone,
  isSelected,
  onToggle,
  onMove,
}: MobileOrderCardProps) {
  const loads = getShipmentLoads(item);
  const routeName = getRouteName(item);
  const driverName = item?.driver_id?.name || "";
  const originLine = formatOriginWithInvoices(item);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.25,
        borderColor: isSelected ? "#7ec597" : "#e5e9ef",
        bgcolor: isSelected ? "#f7fcf9" : "#fff",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", minHeight: "100%" }}>
        <Box sx={{ width: 4, flexShrink: 0, bgcolor: stageColor }} />
        <Box sx={{ flex: 1, p: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
            }}
          >
            <Checkbox
              checked={isSelected}
              onChange={(_event, checked) => onToggle(item.id, checked)}
              sx={{
                p: 0.25,
                mt: -0.25,
                color: "#9ca3af",
                "&.Mui-checked": { color: "#5fa67a" },
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#1f2937", wordBreak: "break-word" }}
              >
                {item?.name}
              </Typography>
              {item?.scheduled_date && (
                <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                  {formatScheduledDate(item.scheduled_date, userTimezone)}
                </Typography>
              )}
              {item?.partner_id?.name && (
                <Typography
                  variant="body2"
                  sx={{ color: "#374151", fontWeight: 600, mt: 0.5 }}
                >
                  {item.partner_id.name}
                </Typography>
              )}
              {originLine && (
                <Typography
                  variant="caption"
                  sx={{ color: "#6b7280", display: "block", mt: 0.35 }}
                >
                  {originLine}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                {loads > 0 && (
                  <Chip
                    size="small"
                    label={`${loads} load${loads === 1 ? "" : "s"}`}
                    sx={{ height: 24, bgcolor: "#eef7f1", color: "#3d6b52", fontWeight: 600 }}
                  />
                )}
                {routeName && (
                  <Chip
                    size="small"
                    label={routeName}
                    sx={{ height: 24, bgcolor: "#f3f4f6", fontWeight: 600 }}
                  />
                )}
                {driverName && (
                  <Chip
                    size="small"
                    label={driverName}
                    sx={{ height: 24, bgcolor: "#f3f4f6", fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
            <IconButton
              size="small"
              aria-label="Move delivery area"
              onClick={() => onMove(item)}
              sx={{ color: "#64748b", mt: -0.25 }}
            >
              <DriveFileMoveIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Card>
  );
});
